import React, { useState } from 'react';
import {
    Box,
    Grid,
    TextField,
    FormControl,
    Typography,
    Button,
    FormControlLabel,
    Checkbox,
    Select,
    MenuItem,
    Card,
    CardContent,
    IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { allRegion, TruckType, TrailerType } from '../../utils/dropOptions';

import AddressForm from '../../components/address-form/AddressForm';


function Employment({ onNext }) {
    const [employers, setEmployers] = useState([{
        startDate: null,
        endDate: null,
        employerName: '',
        address: {
            line1: '',
            line2: '',
            city: '',
            stateProvince: '',
            postalCode: '',
            country: '',
        },
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        positionHeld: '',
        reasonForLeaving: '',
        wasTerminated: false,
        mayContact: false,
        operatedCommercialVehicle: false,
        subjectToFMCSR: false,
        performedSafetySensitiveFunctions: false,
        regionsOperated: '',
        averageMilesPerWeek: '',
        truckType: '',
        trailerType: '',
        crossBorder: false,
    }]);

    const handleChange = (index) => (field) => (event) => {
        const newEmployers = [...employers];
        newEmployers[index] = {
            ...newEmployers[index],
            [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
        };
        setEmployers(newEmployers);
    };

    const handleDateChange = (index) => (field) => (date) => {
        const newEmployers = [...employers];
        newEmployers[index] = {
            ...newEmployers[index],
            [field]: date
        };
        setEmployers(newEmployers);
    };

    const handleAddressChange = (index) => (field, value) => {
        const newEmployers = [...employers];
        newEmployers[index] = {
            ...newEmployers[index],
            address: {
                ...newEmployers[index].address,
                [field]: value
            }
        };
        setEmployers(newEmployers);
    };

    const addEmployer = () => {
        setEmployers([...employers, {
            startDate: null,
            endDate: null,
            employerName: '',
            address: {
                line1: '',
                line2: '',
                city: '',
                stateProvince: '',
                postalCode: '',
                country: '',
            },
            contactPerson: '',
            contactEmail: '',
            contactPhone: '',
            positionHeld: '',
            reasonForLeaving: '',
            wasTerminated: false,
            mayContact: false,
            operatedCommercialVehicle: false,
            subjectToFMCSR: false,
            performedSafetySensitiveFunctions: false,
            regionsOperated: '',
            averageMilesPerWeek: '',
            truckType: '',
            trailerType: '',
            crossBorder: false,
        }]);
    };

    const removeEmployer = (index) => {
        if (employers.length > 1) {
            setEmployers(employers.filter((_, i) => i !== index));
        }
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Employment History</Typography>
                <Button
                    variant="outlined"
                    onClick={addEmployer}
                    startIcon={<AddIcon />}
                >
                    Add Employer
                </Button>
            </Box>

            {employers.map((employer, index) => (
                <Card variant="outlined" sx={{ mb: 3 }} key={index}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1">Employer {index + 1}</Typography>
                            {employers.length > 1 && (
                                <IconButton onClick={() => removeEmployer(index)} size="small">
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Box>

                        <Grid container spacing={3}>
                            {/* Dates */}
                            <Grid item xs={12} sm={6}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Start Date"
                                        value={employer.startDate}
                                        onChange={handleDateChange(index)('startDate')}
                                        renderInput={(params) => <TextField {...params} fullWidth required />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="End Date"
                                        value={employer.endDate}
                                        onChange={handleDateChange(index)('endDate')}
                                        renderInput={(params) => <TextField {...params} fullWidth />}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            {/* Employer Details */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Employer Name"
                                    value={employer.employerName}
                                    onChange={handleChange(index)('employerName')}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="subtitle1" gutterBottom>Employer Address</Typography>
                                <AddressForm
                                    address={employer.address}
                                    onChange={handleAddressChange(index)}
                                />
                            </Grid>

                            {/* Contact Information */}
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Contact Person"
                                    value={employer.contactPerson}
                                    onChange={handleChange(index)('contactPerson')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Contact Email"
                                    type="email"
                                    value={employer.contactEmail}
                                    onChange={handleChange(index)('contactEmail')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Contact Phone"
                                    value={employer.contactPhone}
                                    onChange={handleChange(index)('contactPhone')}
                                />
                            </Grid>

                            {/* Position and Status */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Position Held"
                                    value={employer.positionHeld}
                                    onChange={handleChange(index)('positionHeld')}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Reason for Leaving"
                                    value={employer.reasonForLeaving}
                                    onChange={handleChange(index)('reasonForLeaving')}
                                />
                            </Grid>

                            {/* Checkboxes */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={employer.wasTerminated}
                                            onChange={handleChange(index)('wasTerminated')}
                                        />
                                    }
                                    label="Were you terminated/discharged/laid off?"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={employer.mayContact}
                                            onChange={handleChange(index)('mayContact')}
                                        />
                                    }
                                    label="May we contact this employer?"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={employer.operatedCommercialVehicle}
                                            onChange={handleChange(index)('operatedCommercialVehicle')}
                                        />
                                    }
                                    label="Did you operate a commercial vehicle?"
                                />
                            </Grid>

                            {/* Commercial Vehicle Section */}
                            {employer.operatedCommercialVehicle && (
                                <>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1">Commercial Vehicle Details</Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={employer.subjectToFMCSR}
                                                    onChange={handleChange(index)('subjectToFMCSR')}
                                                />
                                            }
                                            label="Subject to Federal Motor Carrier or Transport Canada Safety Regulations"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={employer.performedSafetySensitiveFunctions}
                                                    onChange={handleChange(index)('performedSafetySensitiveFunctions')}
                                                />
                                            }
                                            label="Performed safety-sensitive functions subject to drug and alcohol testing"
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <Select
                                                value={employer.regionsOperated}
                                                onChange={handleChange(index)('regionsOperated')}
                                                displayEmpty
                                            >
                                                <MenuItem value="" disabled>Select Region Operated</MenuItem>
                                                {allRegion.map((region) => (
                                                    <MenuItem key={region} value={region}>
                                                        {region.replace(/_/g, ' ').toUpperCase()}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Average Miles per Week"
                                            type="number"
                                            value={employer.averageMilesPerWeek}
                                            onChange={handleChange(index)('averageMilesPerWeek')}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <Select
                                                value={employer.truckType}
                                                onChange={handleChange(index)('truckType')}
                                                displayEmpty
                                            >
                                                <MenuItem value="" disabled>Select Truck Type</MenuItem>
                                                {TruckType.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {type.replace(/_/g, ' ').toUpperCase()}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <Select
                                                value={employer.trailerType}
                                                onChange={handleChange(index)('trailerType')}
                                                displayEmpty
                                            >
                                                <MenuItem value="" disabled>Select Trailer Type</MenuItem>
                                                {TrailerType.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {type.replace(/_/g, ' ').toUpperCase()}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={employer.crossBorder}
                                                    onChange={handleChange(index)('crossBorder')}
                                                />
                                            }
                                            label="Cross Border Operations"
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </CardContent>
                </Card >
            ))
            }

            <Box sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={onNext}
                >
                    Continue
                </Button>
            </Box>
        </Box >
    );
}

export default Employment;
